import { useMutation } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { Block, BlockTitle, Button, List, ListItem, Sheet } from "framework7-react";
import { useCallback, useMemo, useState } from "react";
import invariant from "ts-invariant";

import { CategoryChip } from "@/components/CategoryChip";
import { createPage, PageComponentProps } from "@/factories/createPage";
import { getFragmentData } from "@/generated/graphql";
import {
  CategoryEnum,
  EntryFragmentDoc,
  GetEntryDocument,
  UpdateEntryCategoryDocument,
} from "@/generated/graphql/graphql";
import { useSheetPageReady } from "@/hooks/useSheetPageReady";

const CATEGORIES: CategoryEnum[] = Object.values(CategoryEnum).filter(
  (c) => c !== CategoryEnum.Unset,
) as CategoryEnum[];

export const ChangeCategorySheetPage = createPage("ChangeCategorySheetPage", PageComponent);

function PageComponent({ pageName, f7route, f7router }: PageComponentProps) {
  const { getPageProps, swipeHandler, isPageReady } = useSheetPageReady(pageName);
  const close = useCallback(() => f7router.back(), [f7router]);

  const entryId = f7route.params.entryId as string;
  const { data } = useQuery(GetEntryDocument, {
    skip: !isPageReady,
    variables: { id: entryId },
  });
  const entry = getFragmentData(EntryFragmentDoc, data?.entry);

  const initialCategory = useMemo<CategoryEnum | undefined>(() => entry?.categoryKey, [entry]);
  const [selectedCategory, setSelectedCategory] = useState<CategoryEnum | undefined>(initialCategory);

  const [updateCategory, { loading }] = useMutation(UpdateEntryCategoryDocument, {
    onCompleted: close,
  });

  const save = async () => {
    invariant(selectedCategory, "selectedCategory is not defined");
    invariant(entry, "entry is not defined");
    await updateCategory({
      variables: {
        entryCategoryInput: { categoryKey: selectedCategory },
        entryId: entry.id,
      },
    });
  };

  return (
    <Sheet
      data-component={pageName}
      {...getPageProps()}
    >
      {swipeHandler}
      <BlockTitle
        large
        className="no-margin-top"
      >
        カテゴリを選ぶ
      </BlockTitle>

      <List>
        {CATEGORIES.map((category) => (
          <ListItem
            key={category}
            link={false}
            onClick={() => setSelectedCategory(category)}
          >
            <div style={{ alignItems: "center", display: "flex", width: "100%" }}>
              <div style={{ flex: 1 }}>
                <CategoryChip categoryKey={category} />
              </div>
              <div
                style={{
                  color: selectedCategory === category ? "#7A4A2C" : "transparent",
                  textAlign: "right",
                  width: 24,
                }}
              >
                ✓
              </div>
            </div>
          </ListItem>
        ))}
      </List>
      <Block className="grid grid-cols-2 grid-gap">
        <Button
          large
          outline
          onClick={close}
        >
          キャンセル
        </Button>
        <Button
          fill
          large
          preloader
          disabled={!selectedCategory}
          loading={loading}
          onClick={save}
        >
          保存する
        </Button>
      </Block>
    </Sheet>
  );
}
